import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ServiceCard from './components/ServiceCard';
import AssessmentModal from './components/AssessmentModal';
import ServiceRecommendationEngine from './components/ServiceRecommendationEngine';
import RecommendationResults from './components/RecommendationResults';
import TechnologyIntegration from './components/TechnologyIntegration';
import ROICalculator from './components/ROICalculator';

const ServiceEcosystem = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [selectedService, setSelectedService] = useState(null);
  const [showAssessment, setShowAssessment] = useState(false);
  const [recommendations, setRecommendations] = useState(null);
  const [activeTab, setActiveTab] = useState('services');

  const services = [
    {
      id: 'velocity-engineering',
      title: t('serviceEcosystem.services.velocityEngineering.title'),
      subtitle: t('serviceEcosystem.services.velocityEngineering.subtitle'),
      description: t('serviceEcosystem.services.velocityEngineering.description'),
      icon: 'Zap',
      iconBg: 'bg-accent/10',
      iconColor: 'text-accent',
      badge: t('serviceEcosystem.services.velocityEngineering.badge'),
      badgeStyle: 'bg-accent/10 text-accent',
      focusArea: t('serviceEcosystem.services.velocityEngineering.focusArea'),
      avgImpact: t('serviceEcosystem.services.velocityEngineering.avgImpact'),
      capabilities: [
        'Technical debt reduction with measurable ROI tracking',
        'CI/CD pipeline optimization and automation',
        'Code review process enhancement and tooling',
        'Sprint planning optimization with realistic estimation',
        'Production deployment frequency acceleration',
        'Quality gate implementation without velocity loss',
        'Team skill development through pair programming',
        'Performance bottleneck identification and resolution'
      ]
    },
    {
      id: 'ownership-culture',
      title: t('serviceEcosystem.services.ownershipCulture.title'),
      subtitle: t('serviceEcosystem.services.ownershipCulture.subtitle'),
      description: t('serviceEcosystem.services.ownershipCulture.description'),
      icon: 'Users',
      iconBg: 'bg-success/10',
      iconColor: 'text-success',
      badge: t('serviceEcosystem.services.ownershipCulture.badge'),
      badgeStyle: 'bg-success/10 text-success',
      focusArea: t('serviceEcosystem.services.ownershipCulture.focusArea'),
      avgImpact: t('serviceEcosystem.services.ownershipCulture.avgImpact'),
      capabilities: [
        'Ownership mindset development through practical exercises',
        'Decision-making framework implementation',
        'Cross-team collaboration optimization',
        'Conflict resolution and communication training',
        'Performance measurement system design',
        'Leadership development for technical leads',
        'Retrospective process enhancement for action',
        'Team autonomy increase with accountability structures'
      ]
    },
    {
      id: 'architectural-modernization',
      title: t('serviceEcosystem.services.architecturalModernization.title'),
      subtitle: t('serviceEcosystem.services.architecturalModernization.subtitle'),
      description: t('serviceEcosystem.services.architecturalModernization.description'),
      icon: 'Building',
      iconBg: 'bg-warning/10',
      iconColor: 'text-warning',
      badge: t('serviceEcosystem.services.architecturalModernization.badge'),
      badgeStyle: 'bg-warning/10 text-warning',
      focusArea: t('serviceEcosystem.services.architecturalModernization.focusArea'),
      avgImpact: t('serviceEcosystem.services.architecturalModernization.avgImpact'),
      capabilities: [
        'Microservices architecture design and implementation',
        'Legacy system modernization with zero-downtime migration',
        'API-first design patterns and governance',
        'Cloud-native architecture transformation',
        'Observability and monitoring system implementation',
        'Security architecture enhancement and compliance',
        'Performance optimization and scalability planning',
        'Technology stack modernization with risk mitigation'
      ]
    }
  ];

  const tabs = [
    { id: 'services', name: t('serviceEcosystem.tabs.servicesOverview'), icon: 'Layers' },
    { id: 'recommendation', name: t('serviceEcosystem.tabs.getRecommendations'), icon: 'Brain' },
    { id: 'technology', name: t('serviceEcosystem.tabs.technologyStack'), icon: 'Puzzle' },
    { id: 'roi', name: t('serviceEcosystem.tabs.roiCalculator'), icon: 'Calculator' }
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleAssessmentStart = (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    setSelectedService(service);
    setShowAssessment(true);
  };

  const handleLearnMore = (serviceId) => {
    // In a real app, this would navigate to a detailed service page
    console.log('Learn more about service:', serviceId);
  };

  const handleAssessmentComplete = (responses) => {
    console.log('Assessment completed:', responses);
    setShowAssessment(false);
    // In a real app, this would process the assessment and show results
  };

  const handleRecommendation = (recs) => {
    setRecommendations(recs);
  };

  const handleServiceSelect = (serviceId, action) => {
    if (action === 'assessment') {
      handleAssessmentStart(serviceId);
    } else if (action === 'learn-more') {
      handleLearnMore(serviceId);
    }
  };

  const handleStartOver = () => {
    setRecommendations(null);
  };

  const handleDiscoveryCall = () => {
    navigate('/discovery-portal');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <div className="w-8 h-8 bg-accent/10 rounded-lg flex items-center justify-center">
                <Icon name="Layers" size={20} className="text-accent" />
              </div>
              <span className="text-sm font-medium text-accent">{t('serviceEcosystem.subtitle')}</span>
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-text-primary mb-6 text-balance">
              {t('serviceEcosystem.title')}
            </h1>
            <p className="text-xl text-text-secondary max-w-3xl mx-auto text-balance">
              {t('serviceEcosystem.description')}
            </p>
          </div>

          {/* Service Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            <div className="text-center p-6 bg-card rounded-xl border border-border">
              <div className="text-3xl font-bold text-accent mb-2">150+</div>
              <div className="text-sm text-text-secondary">{t('serviceEcosystem.stats.teamsTransformed')}</div>
            </div>
            <div className="text-center p-6 bg-card rounded-xl border border-border">
              <div className="text-3xl font-bold text-success mb-2">35%</div>
              <div className="text-sm text-text-secondary">{t('serviceEcosystem.stats.velocityIncrease')}</div>
            </div>
            <div className="text-center p-6 bg-card rounded-xl border border-border">
              <div className="text-3xl font-bold text-warning mb-2">6 Mo</div>
              <div className="text-sm text-text-secondary">{t('serviceEcosystem.stats.paybackPeriod')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="px-6 lg:px-8 mb-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap gap-2 justify-center">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-all ${
                  activeTab === tab.id
                    ? 'border-accent bg-accent/10 text-accent' :'border-border hover:border-accent/50 text-text-secondary hover:text-text-primary'
                }`}
              >
                <Icon name={tab.icon} size={16} />
                <span className="text-sm font-medium">{tab.name}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Content Sections */}
      <section className="px-6 lg:px-8 pb-16">
        <div className="max-w-7xl mx-auto">
          {activeTab === 'services' && (
            <div className="space-y-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
                {services.map((service) => (
                  <ServiceCard
                    key={service.id}
                    service={service}
                    onAssessmentStart={handleAssessmentStart}
                    onLearnMore={handleLearnMore}
                  />
                ))}
              </div>

              {/* CTA Section */}
              <div className="bg-card rounded-xl border border-border p-8 text-center">
                <h3 className="text-2xl font-semibold text-text-primary mb-4">
                  {t('serviceEcosystem.cta.title')}
                </h3>
                <p className="text-text-secondary mb-6 max-w-2xl mx-auto">
                  {t('serviceEcosystem.cta.description')}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    variant="outline"
                    size="lg"
                    iconName="Calendar"
                    iconPosition="left"
                    onClick={handleDiscoveryCall}
                  >
                    {t('serviceEcosystem.cta.scheduleCall')}
                  </Button>
                  <Button
                    variant="default"
                    size="lg"
                    iconName="ArrowRight"
                    iconPosition="right"
                    onClick={() => setActiveTab('recommendation')}
                    className="bg-cta hover:bg-cta/90 text-cta-foreground"
                  >
                    {t('serviceEcosystem.cta.getRecommendations')}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'recommendation' && (
            <div className="space-y-8">
              <ServiceRecommendationEngine
                services={services}
                onRecommendation={handleRecommendation}
              />
              {recommendations && (
                <RecommendationResults
                  recommendations={recommendations}
                  onServiceSelect={handleServiceSelect}
                  onStartOver={handleStartOver}
                />
              )}
            </div>
          )}

          {activeTab === 'technology' && (
            <TechnologyIntegration />
          )}

          {activeTab === 'roi' && (
            <div className="space-y-8">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-semibold text-text-primary mb-4">
                  {t('serviceEcosystem.tabs.roiCalculator')}
                </h2>
                <p className="text-text-secondary max-w-2xl mx-auto">
                  Use our ROI calculator to estimate the financial impact of our services on your engineering organization.
                </p>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {services.map((service) => (
                  <div key={service.id} className="space-y-4">
                    <div className="text-center">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-3 ${service.iconBg}`}>
                        <Icon name={service.icon} size={24} className={service.iconColor} />
                      </div>
                      <h3 className="font-semibold text-text-primary">{service.title}</h3>
                    </div>
                    <ROICalculator service={service} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Assessment Modal */}
      <AssessmentModal
        isOpen={showAssessment}
        onClose={() => setShowAssessment(false)}
        service={selectedService}
        onComplete={handleAssessmentComplete}
      />

      {/* Footer */}
      <footer className="bg-primary text-primary-foreground py-12 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-8 h-8 bg-accent rounded-lg flex items-center justify-center">
                  <div className="w-4 h-4 bg-primary-foreground rounded-sm"></div>
                </div>
                <div>
                  <span className="text-xl font-bold">SIGNA</span>
                  <span className="text-sm opacity-80 ml-2">Technologies</span>
                </div>
              </div>
              <p className="text-primary-foreground/80 mb-4 max-w-md">
                {t('footer.description')}
              </p>
              <div className="flex space-x-4">
                <Icon name="Mail" size={20} className="text-primary-foreground/60" />
                <Icon name="Phone" size={20} className="text-primary-foreground/60" />
                <Icon name="MapPin" size={20} className="text-primary-foreground/60" />
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">{t('footer.services')}</h4>
              <ul className="space-y-2 text-sm text-primary-foreground/80">
                <li>{t('footer.velocityEngineering')}</li>
                <li>{t('footer.ownershipCulture')}</li>
                <li>{t('footer.architecturalModernization')}</li>
                <li>Team Assessment</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">{t('footer.company')}</h4>
              <ul className="space-y-2 text-sm text-primary-foreground/80">
                <li>{t('footer.aboutUs')}</li>
                <li>{t('footer.caseStudies')}</li>
                <li>Careers</li>
                <li>{t('footer.contact')}</li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-primary-foreground/20 mt-8 pt-8 text-center text-sm text-primary-foreground/60">
            <p>&copy; {new Date().getFullYear()} SIGNA Technologies. {t('footer.allRightsReserved')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ServiceEcosystem;